import { monthName } from '../@commons/@services/front/seed-timing.util';

/**
 * Mon plan de saison (NF-1) : dates en clair et regroupements, sans appel réseau.
 * Les dates viennent du back au format YYYY-MM-DD, pour l'année de la saison.
 */

export interface DateLabel {
  key: string;
  params: { day?: number; month: string };
}

/**
 * Bandeau d'entrée du Potager (K21) : du 8 février au 31 mars, la fenêtre où l'on achète
 * ses graines. Le plan reste accessible toute l'année par sa route.
 */
export function isSeasonPlanBannerPeriod(today: Date = new Date()): boolean {
  const month = today.getMonth() + 1;
  return (month === 2 && today.getDate() >= 8) || month === 3;
}

/** Choix « Pour combien de personnes ? » : la dernière pastille vaut « 5 et plus ». */
export const PERSON_CHOICES = [1, 2, 3, 5];

/** Groupes de l'écran « Qu'est-ce que tu veux récolter ? », dans l'ordre de la maquette. */
export const CULTIVATION_GROUPS: { key: string; groupTypes: string[] }[] = [
  { key: 'fruits', groupTypes: ['FRUIT_VEGETABLES', 'FRUITS'] },
  { key: 'leaves', groupTypes: ['LEAF_VEGETABLES', 'STEM_VEGETABLES'] },
  { key: 'roots', groupTypes: ['ROOT_VEGETABLES', 'BULB_VEGETABLES'] },
  { key: 'aromatics', groupTypes: ['AROMATICS', 'HERBS'] },
  { key: 'others', groupTypes: [] }, // fleurs, champignons, culture sans groupe
];

export function groupKeyOf(groupType: string | undefined | null): string {
  return CULTIVATION_GROUPS.find((g) => g.groupTypes.includes(groupType ?? ''))?.key ?? 'others';
}

const parts = (isoDate: string) => {
  const [year, month, day] = isoDate.substring(0, 10).split('-').map(Number);
  return { year, month, day };
};

export function monthOf(isoDate: string): number {
  return parts(isoDate).month;
}

/** « Début mars », « Mi-mars », « Fin mars » : la quinzaine suffit, le jardin n'est pas au jour près. */
export function periodLabel(isoDate: string, lang: string): DateLabel {
  const { month, day } = parts(isoDate);
  const third = day <= 10 ? 'early' : day <= 20 ? 'mid' : 'late';
  return { key: `season-plan.when.${third}`, params: { month: monthName(month, lang) } };
}

/** « Vers le 20 février » : le premier geste, mis en avant, garde son jour. */
export function aroundLabel(isoDate: string, lang: string): DateLabel {
  const { month, day } = parts(isoDate);
  return { key: 'season-plan.when.around', params: { day, month: monthName(month, lang) } };
}

/** Regroupe des éléments datés par mois (1 à 12), dans l'ordre de l'année, mois vides omis. */
export function groupByMonth<T extends { date: string }>(items: T[]): { month: number; items: T[] }[] {
  const byMonth = new Map<number, T[]>();
  [...items]
    .sort((a, b) => a.date.localeCompare(b.date))
    .forEach((item) => {
      const month = monthOf(item.date);
      byMonth.set(month, [...(byMonth.get(month) ?? []), item]);
    });
  return [...byMonth.entries()].sort(([a], [b]) => a - b).map(([month, list]) => ({ month, items: list }));
}

/**
 * Liste de courses en texte, pour la copier là où l'impression n'existe pas (coque mobile) :
 * une ligne par culture à acheter, avec le lien du webshop quand il existe.
 */
export function shoppingListText(title: string, lines: { name: string; url?: string }[]): string {
  return [title, '', ...lines.map((l) => `- ${l.name}${l.url ? ` : ${l.url}` : ''}`)].join('\n');
}
